import { useEffect, useState } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Clock, GraduationCap, Home, Users, TrendingUp, HelpCircle } from 'lucide-react';
import { getNeighbourhood, getListings, trackEvent } from '../lib/api';
import SEO from '../components/SEO';

export default function NeighbourhoodDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listingCount, setListingCount] = useState(0);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: `neighbourhood_${slug}` });
    getNeighbourhood(slug).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    getListings({ neighbourhood: slug, limit: 1 }).then(r => setListingCount(r.data.total || 0)).catch(() => {});
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-16"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-16"><p className="text-slate-500">Neighbourhood not found</p><Link to="/neighbourhoods"><Button variant="outline" className="rounded-full">Back</Button></Link></div>;

  const priceFrom = data?.price_bands?.detached?.min > 0 ? data.price_bands.detached.min : null;

  return (
    <div className="min-h-screen pt-16" data-testid="neighbourhood-detail-page">
      {data && (
        <SEO
          title={`${data.name}, Burlington — Housing Prices, Schools & Neighbourhood Guide`}
          description={`${data.name} in Burlington, Ontario: detached homes from ${priceFrom ? formatPrice(priceFrom) : 'varies'}. ${data.vibe_summary} Schools, commute times, pricing, pros and cons for homebuyers.`}
          canonical={`/neighbourhoods/${slug}`}
          type="article"
          article={{ dateModified: data.updated_at || data.created_at }}
          breadcrumbs={[
            { name: 'Home', url: '/' },
            { name: 'Neighbourhoods', url: '/neighbourhoods' },
            { name: data.name, url: `/neighbourhoods/${slug}` },
          ]}
          faq={data.faq}
        />
      )}

      {/* Hero */}
      <section className="mesh-gradient-section py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/neighbourhoods" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-neighbourhoods">
            <ArrowLeft className="w-4 h-4 mr-1" /> Neighbourhoods
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">Burlington</span>
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
            {data.name}
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed">{data.vibe_summary}</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {data.tags?.filter(t => !t.startsWith('has_')).map(tag => (
              <span key={tag} className="text-[11px] font-mono bg-white border border-slate-200/80 text-slate-500 px-3 py-1 rounded-full">{tag.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-12 md:py-16">
          <div className="lg:col-span-8 space-y-14">

            {/* SEO Description */}
            {data.seo_description && (
              <div>
                <p className="text-base text-slate-600 leading-relaxed">{data.seo_description}</p>
              </div>
            )}

            {/* History */}
            {data.history && (
              <div className="border-t border-slate-100 pt-10">
                <h2 className="text-2xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>History of {data.name}</h2>
                <p className="text-[15px] text-slate-500 leading-relaxed">{data.history}</p>
              </div>
            )}

            {/* Price Ranges */}
            <div className="border-t border-slate-100 pt-10">
              <h2 className="text-2xl tracking-tight mb-5" style={{ fontFamily: "'Instrument Serif', serif" }}>Price ranges</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Detached', band: data.price_bands?.detached },
                  { label: 'Townhome', band: data.price_bands?.town },
                  { label: 'Condo', band: data.price_bands?.condo },
                ].map(item => item.band?.min > 0 && (
                  <div key={item.label} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">{item.label}</p>
                    <p className="font-mono text-lg font-semibold text-slate-900">
                      {formatPriceRange(item.band.min, item.band.max)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* School Breakdown */}
            {data.school_breakdown && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <GraduationCap className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>School breakdown</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.school_breakdown.public?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                      <p className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600 mb-3">Public (HDSB)</p>
                      <div className="space-y-2.5">
                        {data.school_breakdown.public.map((s, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></div>
                            <span className="text-[13px] text-slate-600 leading-relaxed">{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.school_breakdown.catholic?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                      <p className="text-[10px] font-semibold tracking-widest uppercase text-blue-600 mb-3">Catholic (HCDSB)</p>
                      <div className="space-y-2.5">
                        {data.school_breakdown.catholic.map((s, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5"></div>
                            <span className="text-[13px] text-slate-600 leading-relaxed">{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-3 italic">School catchment boundaries change. Always verify with the board directly before purchasing.</p>
              </div>
            )}

            {/* Commute Times */}
            {data.commute_times && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <Clock className="w-5 h-5 text-slate-500" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Commute times</h2>
                </div>
                <div className="bento-grid grid-cols-1 md:grid-cols-3 rounded-2xl">
                  <div className="p-5">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">GO Transit</p>
                    <p className="text-[13px] text-slate-700 leading-relaxed">{data.commute_times.go_transit}</p>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Highway</p>
                    <p className="text-[13px] text-slate-700 leading-relaxed">{data.commute_times.highway}</p>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Downtown Burlington</p>
                    <p className="text-[13px] text-slate-700 leading-relaxed">{data.commute_times.downtown_burlington}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Lot Sizes & Architecture */}
            {(data.lot_sizes || data.architectural_styles) && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <Home className="w-5 h-5 text-slate-500" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Lots & architecture</h2>
                </div>
                {data.lot_sizes && (
                  <div className="mb-6">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Typical lot sizes</p>
                    <p className="text-[15px] text-slate-500 leading-relaxed">{data.lot_sizes}</p>
                  </div>
                )}
                {data.architectural_styles && (
                  <div>
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Architectural styles</p>
                    <p className="text-[15px] text-slate-500 leading-relaxed">{data.architectural_styles}</p>
                  </div>
                )}
              </div>
            )}

            {/* Who this area is best for */}
            {data.best_for?.length > 0 && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Who {data.name} is best for</h2>
                </div>
                <div className="space-y-2.5">
                  {data.best_for.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-[14px] text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pros & Cons */}
            <div className="border-t border-slate-100 pt-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.pros?.length > 0 && (
                  <div className="bg-emerald-50/50 rounded-2xl p-6 border border-emerald-100/50">
                    <h3 className="text-lg tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>Strengths</h3>
                    <div className="space-y-2">
                      {data.pros.map((p, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-[13px] text-slate-600">{p}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {data.cons?.length > 0 && (
                  <div className="bg-amber-50/50 rounded-2xl p-6 border border-amber-100/50">
                    <h3 className="text-lg tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>Tradeoffs</h3>
                    <div className="space-y-2">
                      {data.cons.map((c, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-[13px] text-slate-600">{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Market Trends */}
            {data.market_trends && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-5 h-5 text-slate-500" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Market trends</h2>
                </div>
                <p className="text-[15px] text-slate-500 leading-relaxed">{data.market_trends}</p>
              </div>
            )}

            {/* CTA mid-page */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Is {data.name} right for your family?</h3>
                <p className="text-sm text-slate-500">See how this area scores against your specific budget, timeline, and priorities.</p>
              </div>
              <Link to="/blueprint" className="flex-shrink-0" data-testid="neighbourhood-mid-cta">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                  Start My Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* FAQ */}
            {data.faq?.length > 0 && (
              <div className="border-t border-slate-100 pt-10">
                <div className="flex items-center gap-2 mb-5">
                  <HelpCircle className="w-5 h-5 text-slate-500" />
                  <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Frequently asked questions</h2>
                </div>
                <div className="space-y-5">
                  {data.faq.map((item, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-6 mb-6 last:mb-0">
                      <p className="text-[14px] font-medium text-slate-900 mb-2">{item.q}</p>
                      <p className="text-[13px] text-slate-500 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-8">
              <div className="bg-slate-950 noise-overlay relative rounded-2xl overflow-hidden p-6">
                <div className="relative z-10">
                  <h3 className="text-lg text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    Is {data.name} <span className="italic">right for you?</span>
                  </h3>
                  <p className="text-[13px] text-white/40 mb-6">
                    Take the Buyer Blueprint to see how this area scores against your specific needs, budget, and timeline.
                  </p>
                  <Link to="/blueprint" data-testid="neighbourhood-detail-cta">
                    <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full py-3 font-medium h-auto text-sm hover:-translate-y-px transition-all mb-3">
                      Start Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/homes-for-sale?neighbourhood=${slug}`}>
                    <Button variant="outline" className="w-full rounded-full py-3 font-medium h-auto text-sm border-white/20 text-white/70 hover:text-white hover:bg-white/10">
                      View Homes for Sale
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Quick facts sidebar */}
              {data.commute_times && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-10">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Quick facts</p>
                  <div className="space-y-3 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-slate-400">GO to Toronto</span>
                      <span className="text-slate-700 font-medium text-right">{data.commute_times.go_transit?.split('→')[1]?.trim() || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">To downtown</span>
                      <span className="text-slate-700 font-medium">{data.commute_times.downtown_burlington}</span>
                    </div>
                    {priceFrom && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">From</span>
                        <span className="font-mono text-slate-700 font-medium">{formatPrice(priceFrom)}</span>
                      </div>
                    )}
                    {listingCount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">For sale</span>
                        <Link to={`/neighbourhoods/${slug}/homes-for-sale`} className="font-mono text-emerald-600 font-medium hover:text-emerald-700">{listingCount} listings</Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
